<template>
    <div class="mt-3">
        <Stepper :steps="steps" ref="stepper" :key="stepperKey">
            <template v-slot:paso-tipo>
                <b-row>
                    <b-col cols="12">
                        <h4 class="mb-4">Selecciona el tipo de importación</h4>
                        <div class="form-group">
                            <label for="tipoImportacion">Tipo de importación</label>
                            <select 
                                id="tipoImportacion"
                                v-model="tipoImportacion" 
                                class="form-control"
                                @change="resetImportData"
                            >
                                <option value="">Selecciona una opción</option>
                                <option value="csv">Archivo CSV</option>
                                <option value="textarea">Texto plano</option>
                            </select>
                            <small class="form-text text-muted">
                                Selecciona si quieres importar desde un archivo o pegando texto directamente
                            </small>
                        </div>
                    </b-col>
                    <b-col cols="12" class="text-right mt-4">
                        <b-button 
                            variant="primary" 
                            @click="goToNextStep"
                            :disabled="!tipoImportacion"
                        >
                            Continuar
                            <i class="fas fa-arrow-right ml-2"></i>
                        </b-button>
                    </b-col>
                </b-row>
            </template>

            <template v-slot:paso-datos>
                <b-row>
                    <b-col cols="12">
                        <div v-if="tipoImportacion === 'csv'">
                            <h4 class="mb-4">Selecciona el archivo a importar</h4>
                            <DropzoneFileUploader
                                :acceptedFiles="'.csv,.xlsx,.xls'"
                                :maxFiles="1"
                                :maxFilesize="10"
                                message="Arrastra tu archivo CSV aquí, o haz clic para seleccionarlo"
                                @file-added="handleFileUpload"                                
                                ref="dropzoneUploader"
                            />
                        </div>

                        <div v-if="tipoImportacion === 'textarea'">
                            <h4 class="mb-4">Pega el texto a importar</h4>
                            <div class="form-group">
                                <label for="textoImportacion">Texto de importación</label>
                                <textarea 
                                    id="textoImportacion"
                                    v-model="textoImportacion"
                                    class="form-control"
                                    rows="10"
                                    placeholder="Pega aquí el texto con los datos de los subscriptores...Email ,Teléfono ,Nombre ,Apellidos, "
                                ></textarea>
                                <small class="form-text text-muted">
                                    Pega el texto con los datos de los subscriptores. Cada línea debe contener los datos de un subscriptor en este orden: Email ,Teléfono ,Nombre ,Apellidos.
                                    <br/>
                                    Líneas detectadas: <strong>{{ contarLineas }}</strong>
                                </small>
                            </div>
                        </div>
                    </b-col>
                    <b-col cols="12" class="text-right mt-4">
                        <b-button variant="outline-secondary" class="mr-2" @click="goBackStep">
                            <i class="fas fa-arrow-left mr-2"></i>
                            Volver
                        </b-button>
                        <b-button 
                            variant="primary" 
                            @click="goToNextStep"
                            :disabled="!isDatosValid"
                        >
                            Continuar
                            <i class="fas fa-arrow-right ml-2"></i>
                        </b-button>
                    </b-col>
                </b-row>
            </template>

            <template v-slot:paso-confirmacion>
                <b-row>
                    <b-col cols="12">
                        <h4 class="mb-4">Confirmar importación</h4>
                        
                        <div class="alert alert-info mt-3">
                            <h5><i class="fas fa-info-circle mr-2"></i>Resumen de importación</h5>
                            <p><strong>Grupo destino:</strong> {{ grupo.nombre }}</p>
                            <p><strong>Tipo:</strong> {{ tipoImportacion === 'csv' ? 'Archivo CSV' : 'Texto plano' }}</p>
                            <p v-if="tipoImportacion === 'csv' && selectedFile">
                                <strong>Archivo:</strong> {{ selectedFile.name }}
                            </p>
                            <p v-if="tipoImportacion === 'textarea' && textoImportacion">
                                <strong>Líneas de texto:</strong> {{ contarLineas }}
                            </p>
                        </div>
                    </b-col>
                    <b-col v-if="errores" cols="12">
                        <b-alert show variant="danger">
                            <h3>{{ errores.message }}</h3>
                            <ul>
                                <li v-for="(error, index) in errores.errors" :key="index">
                                    {{ error }}
                                </li>
                            </ul>
                        </b-alert>
                    </b-col>
                    <b-col cols="12" class="text-right mt-4">
                        <b-button variant="outline-secondary" class="mr-2" @click="goBackStep">
                            <i class="fas fa-arrow-left mr-2"></i>
                            Volver
                        </b-button>
                        <b-button 
                            variant="success" 
                            @click="handleImportacion"
                            :disabled="isProcessing"
                        >
                            <span v-if="isProcessing">
                                <i class="fas fa-spinner fa-spin mr-2"></i>Procesando...
                            </span>
                            <span v-else>
                                <i class="fas fa-upload mr-2"></i>
                                Finalizar importación
                            </span>
                        </b-button>
                    </b-col>
                </b-row>
            </template>
        </Stepper>
    </div>
</template>

<script>
    import DropzoneFileUploader from "@/components/common/DropZoneFileUploader.vue";
    import Stepper from '@/components/frontend/events/compra/components/Stepper';
    import apiService from '@/services/api-comunicados';

    export default {
        name: 'GruposImportarTab',
        components: {
            DropzoneFileUploader,
            Stepper
        },
        props: {
            grupo: {
                type: Object,
                required: true
            }
        },
        data() {
            return {
                stepperKey: 0,
                steps: [
                    { label: 'Tipo de importación', slot: 'paso-tipo' },
                    { label: 'Datos a importar', slot: 'paso-datos' },
                    { label: 'Confirmar importación', slot: 'paso-confirmacion' }
                ],
                tipoImportacion: '',
                fileUploaded: false,
                selectedFile: null,
                textoImportacion: '',
                isProcessing: false,
                errores: null
            };
        },
        computed: {
            isDatosValid() {
                if (this.tipoImportacion === 'csv') {
                    return this.fileUploaded && this.selectedFile;
                } else if (this.tipoImportacion === 'textarea') {
                    return this.textoImportacion && this.textoImportacion.trim();
                }
                return false;
            },
            contarLineas() {
                if (!this.textoImportacion) return 0;
                return this.textoImportacion.trim().split('\n').filter(line => line.trim()).length;
            }
        },
        methods: {
            goToNextStep() {
                if (this.$refs.stepper && this.$refs.stepper.changeStep) {
                    this.$refs.stepper.changeStep();
                }
            },
            goBackStep() {
                if (this.$refs.stepper && this.$refs.stepper.changeStep) {
                    this.$refs.stepper.changeStep(true);
                }
            },
            resetImportData() {
                this.fileUploaded = false;
                this.selectedFile = null;
                this.textoImportacion = '';
                
                if (this.$refs.dropzoneUploader) {
                    this.$refs.dropzoneUploader.removeAllFiles();
                }
            },
            handleFileUpload(file) {
                if (!file) {
                    this.selectedFile = null;
                    this.fileUploaded = false;
                    return;
                }
                if (!file.name) {
                    this.$toast.error("El formato del archivo no es válido");
                    this.fileUploaded = false;
                    this.selectedFile = null;
                    return;
                }
                const fileName = file.name.toLowerCase();
                const validExtensions = ['.csv'];
                const hasValidExtension = validExtensions.some(ext => 
                    fileName.endsWith(ext) || 
                    fileName.endsWith(ext.substring(1)) 
                );
            
                if (!hasValidExtension) {
                    this.$toast.error("Solo se permiten archivos CSV");
                    this.fileUploaded = false;
                    this.selectedFile = null;
                    return;
                }
                this.selectedFile = file;
                this.fileUploaded = true;
                this.$toast.success(`Archivo subido correctamente: ${file.name}`);
            },
            async handleImportacion() {
                if (!this.grupo || !this.grupo.id) {
                    this.$toast.error('Error: No se pudo identificar el grupo.');
                    return;
                }

                if (this.tipoImportacion === 'csv' && (!this.selectedFile)) {
                    this.$toast.error('Debes subir un archivo.');
                    return;
                }

                if (this.tipoImportacion === 'textarea' && (!this.textoImportacion || !this.textoImportacion.trim())) {
                    this.$toast.error('Debes agregar texto para importar.');
                    return;
                }

                this.isProcessing = true;
                try {
                    const formData = new FormData();
                    formData.append('tipo', this.tipoImportacion);
                    
                    if (this.tipoImportacion === 'csv') {
                        formData.append('archivo', this.selectedFile);
                    } else if (this.tipoImportacion === 'textarea') {
                        formData.append('texto', this.textoImportacion);
                    }

                    const response = await apiService.importarSubscriptoresAGrupo(this.grupo.id, formData);
                        
                    this.$toast.success("Importación completada con éxito");
                    
                    this.$emit('importacion-completada');

                    setTimeout(() => {
                        this.resetForm();
                    }, 500);
                    
                } catch (error) { 
                    this.errores = error.response ? error.response.data : error.message;
                } finally {
                    this.isProcessing = false;
                }
            },
            resetForm() {
                this.tipoImportacion = '';
                this.resetImportData();
                
                this.stepperKey++;
            }
        }
    };
</script>

<style scoped>
.alert ul {
    padding-left: 20px;
    margin-top: 5px;
}

h4 {
    color: #495057;
}
</style>